import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faSearch, 
  faFilter, 
  faDownload, 
  faEye,
  faEdit,
  faTrash,
  faCalendarAlt,
  faExclamationTriangle,
  faCheckCircle,
  faClock,
  faEllipsisV,
  faRefresh,
  faUpload,
  faChartBar,
  faSort,
  faSortUp,
  faSortDown,
  faTimesCircle,
  faSpinner,
  faFileExcel,
  faTimes,
  faBoxOpen,
  faSync,
  faCheck,
  faPercent,
  faBarcode,
  faTags,
  faChevronLeft,
  faChevronRight,
  faPaperPlane,
  faUserCheck,
  faEnvelope,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import { 
  getArticles, 
  exportArticles, 
  deleteArticle, 
  getArticleStats, 
  syncFromPerfionAPI
} from '../services/articleApi';
import { getSuppliers } from '../services/api';

import { SupplierArticle, ArticleFilters, ArticleStats, ArticleStatus } from '../types/article';
import { useToast } from '../components/ui/ToastProvider';
import ArticleImportModal from '../components/articles/ArticleImportModal';
import ArticleDetailModal from '../components/articles/ArticleDetailModal';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import articleWorkflowApi, { CreateArticleRequestData, ReviewSubmissionData } from '../services/articleWorkflowApi';

const Articles: React.FC = () => {
  // **HOOKS**
  const { showSuccess, showWarning, showError } = useToast();
  
  // **STATE MANAGEMENT**
  const [articles, setArticles] = useState<SupplierArticle[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [linkedSuppliers, setLinkedSuppliers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // **STATISTICS**
  const [stats, setStats] = useState<ArticleStats>({
    total: 0,
    draft: 0,
    inProgress: 0,
    submitted: 0,
    approved: 0,
    overdue: 0,
    averageCompletionRate: 0
  });

  // **PAGINATION & FILTERS**
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  
  const [filters, setFilters] = useState<ArticleFilters>({
    search: '',
    status: 'all',
    supplierId: 'all',
    ekCode: 'all',
    category: 'all',
    overdue: false,
    completionMin: 0,
    completionMax: 100,
    showDeleted: false,
    linkedSuppliersOnly: false
  });

  // **SEARCH DEBOUNCING** - Separate state for immediate UI updates
  const [searchTerm, setSearchTerm] = useState(filters.search);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // **SORTING**
  const [sortBy, setSortBy] = useState<string>('updatedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // **MODALS**
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);

  // **DELETE MODAL STATES**
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModalData, setDeleteModalData] = useState<{
    type: 'single' | 'bulk';
    articleId?: string;
    articleName?: string;
    count?: number;
  } | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // **SELECTION**
  const [selectedArticles, setSelectedArticles] = useState<string[]>([]);

  // **ACTIONS**
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);

  // **WORKFLOW STATES**
  const [showWorkflowRequestModal, setShowWorkflowRequestModal] = useState(false);
  const [showWorkflowReviewModal, setShowWorkflowReviewModal] = useState(false);
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<any>(null);

  // **WORKFLOW FORMS**
  const [workflowRequestForm, setWorkflowRequestForm] = useState<CreateArticleRequestData>({
    articleIds: [],
    requestedFields: ['name', 'description', 'brand', 'gtin', 'weight', 'dimensions', 'category', 'price'], // Alle Felder standardmäßig ausgewählt
    message: '',
    priority: 'medium'
  });

  const [workflowReviewForm, setWorkflowReviewForm] = useState<ReviewSubmissionData>({
    action: 'approved',
    comment: '',
    requestedChanges: []
  });

  // **LOAD DATA**
  const loadData = useCallback(async (isSearch = false) => {
    console.log('📊 Loading articles with filters:', filters);
    try {
      if (isSearch) {
        setSearching(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      const [articlesResponse, suppliersResponse] = await Promise.all([
        getArticles({
          page: currentPage,
          limit: itemsPerPage,
          sortBy,
          sortOrder,
          ...filters
        }),
        getSuppliers({ limit: 50 }) // Load only first 50 suppliers for dropdown filter
      ]);

      setArticles(articlesResponse.articles || []);
      setTotalPages(articlesResponse.pagination?.pages || 0);
      setTotalItems(articlesResponse.pagination?.total || 0);
      
      setSuppliers(suppliersResponse.data?.suppliers || suppliersResponse.data || []);
      // Linked suppliers laden wir nur bei Bedarf (wenn der Filter aktiviert wird)
      setLinkedSuppliers([]);
      
      // **DEBUG: LOG LOADED DATA**
      console.log('📊 Articles - Loaded data:', {
        suppliers: (suppliersResponse.data?.suppliers || suppliersResponse.data || []).length,
        articles: (articlesResponse.articles || []).length,
        firstSupplier: (suppliersResponse.data?.suppliers || suppliersResponse.data || [])[0]
      });
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
      setSearching(false);
    }
  }, [currentPage, itemsPerPage, filters, sortBy, sortOrder]);

  // **CALCULATE LIVE STATISTICS FROM FILTERED ARTICLES**
  const liveStats = useMemo(() => {
    const stats = {
      total: totalItems,
      draft: 0,
      inProgress: 0,
      submitted: 0,
      approved: 0,
      overdue: 0,
      averageCompletionRate: 0
    };

    if (articles.length > 0) {
      let totalCompletionRate = 0;
      const now = new Date();

      articles.forEach(article => {
        switch (article.status) {
          case 'draft':
            stats.draft++;
            break;
          case 'in_progress':
            stats.inProgress++;
            break;
          case 'submitted':
            stats.submitted++;
            break;
          case 'approved':
            stats.approved++;
            break;
        }

        // Check if overdue
        if (article.deadline && new Date(article.deadline) < now && article.status !== 'approved') {
          stats.overdue++;
        }

        // Calculate completion rate
        if (article.completionPercentage !== undefined) {
          totalCompletionRate += article.completionPercentage;
        }
      });

      stats.averageCompletionRate = articles.length > 0 ? Math.round(totalCompletionRate / articles.length) : 0;
    }

    return stats;
  }, [articles, totalItems]);

  // **SEARCH DEBOUNCING** - Update filters.search after delay
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      console.log('🔍 Search debounce triggered:', searchTerm);
      setFilters(prev => ({ ...prev, search: searchTerm }));
      setCurrentPage(1);
    }, 300); // 300ms delay

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // **TRIGGER SEARCH WHEN FILTERS CHANGE**
  useEffect(() => {
    const isSearching = filters.search !== '' || searchTerm !== '';
    loadData(isSearching);
  }, [filters, sortBy, sortOrder, currentPage]);

  // **LOAD LINKED SUPPLIERS ONLY WHEN NEEDED**
  const loadLinkedSuppliers = useCallback(async () => {
    if (linkedSuppliers.length === 0) {
      try {
        console.log('📊 Loading linked suppliers for filter with pagination...');
        // Use paginated supplier endpoint with a reasonable limit
        const response = await getSuppliers({ page: 1, limit: 100 });
        const linkedSuppliersData = response.data?.suppliers?.filter((supplier: any) => 
          supplier.glns && supplier.glns.length > 0
        ) || [];
        setLinkedSuppliers(linkedSuppliersData);
      } catch (error) {
        console.error('Error loading linked suppliers:', error);
      }
    }
  }, [linkedSuppliers.length]);

  // **FILTER HANDLERS**
  const handleFilterChange = (key: keyof ArticleFilters, value: any) => {
    console.log('🔍 Filter change:', { key, value, currentFilters: filters });
    
    // Special handling for search - use debounced state
    if (key === 'search') {
      setSearchTerm(value);
      return;
    }
    
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      
      // Automatisch "verknüpfte" Filter zurücksetzen wenn "Alle Lieferanten" gewählt wird
      if (key === 'supplierId' && value === 'all') {
        newFilters.linkedSuppliersOnly = false;
      }
      
      // Load linked suppliers only when linkedSuppliersOnly is activated
      if (key === 'linkedSuppliersOnly' && value === true) {
        loadLinkedSuppliers();
      }
      
      console.log('🔍 New filters (before API call):', newFilters);
      console.log('🔍 linkedSuppliersOnly will be sent as:', newFilters.linkedSuppliersOnly ? 'true' : 'false');
      return newFilters;
    });
    setCurrentPage(1); // Reset to first page when filtering
    setSelectedArticles([]);
  };

  const resetFilters = () => {
    setSearchTerm(''); // Reset search term immediately
    setFilters({
      search: '',
      status: 'all',
      supplierId: 'all',
      ekCode: 'all',
      category: 'all',
      overdue: false,
      completionMin: 0,
      completionMax: 100,
      showDeleted: false,
      linkedSuppliersOnly: false
    });
    setCurrentPage(1);
    setSelectedArticles([]);
  };

  // **SORTING HANDLERS**
  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  // **ACTION HANDLERS**
  const handleRefresh = () => {
    loadData();
  };

  const handleSyncFromPerfion = async () => {
    try {
      setSyncLoading(true);
      showSuccess('Synchronisiere Artikel von Perfion API...');
      
      const result = await syncFromPerfionAPI();
      
      if (result.success) {
        showSuccess(`✅ Perfion Sync erfolgreich: ${result.processed} Artikel verarbeitet, ${result.updated} aktualisiert, ${result.created} neu erstellt`);
        loadData(); // Refresh data after sync
      } else {
        showError(`❌ Perfion Sync fehlgeschlagen: ${result.message}`);
      }
    } catch (error: any) {
      console.error('Error syncing from Perfion:', error);
      showError(`Fehler beim Perfion Sync: ${error.message || 'Unbekannter Fehler'}`);
    } finally {
      setSyncLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      await exportArticles(filters.status !== 'all' ? filters.status : 'all');
      showSuccess('Export erfolgreich gestartet');
    } catch (error: any) {
      console.error('Error exporting articles:', error);
      showError(error.message || 'Fehler beim Export');
    } finally {
      setExportLoading(false);
    }
  };

  // **SINGLE DELETE**
  const handleDeleteArticle = async (articleId: string, articleName: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    setDeleteModalData({
      type: 'single',
      articleId,
      articleName
    });
    setShowDeleteModal(true);
  };

  // **BULK DELETE**
  const handleBulkDelete = () => {
    setDeleteModalData({
      type: 'bulk',
      count: selectedArticles.length
    });
    setShowDeleteModal(true);
  };

  // **DELETE CONFIRM**
  const handleDeleteConfirm = async () => {
    if (!deleteModalData) return;

    try {
      setDeleteLoading(true);
      
      if (deleteModalData.type === 'bulk') {
        await Promise.all(
          selectedArticles.map(id => deleteArticle(id))
        );
        setSelectedArticles([]);
      } else if (deleteModalData.type === 'single' && deleteModalData.articleId) {
        await deleteArticle(deleteModalData.articleId);
      }
      
      await loadData();
      setShowDeleteModal(false);
      setDeleteModalData(null);
      
    } catch (error) {
      console.error('Error deleting article(s):', error);
      setError('Fehler beim Löschen der Artikel');
    } finally {
      setDeleteLoading(false);
    }
  };

  // **BULK STATUS UPDATE**
  const handleBulkStatusUpdate = async (status: ArticleStatus) => {
    if (selectedArticles.length === 0) return;

    try {
      setBulkActionLoading(true);
      
      // TODO: Implement updateArticleStatus API function
      showWarning('Bulk Status Update wird noch implementiert');
      setSelectedArticles([]);
      
    } catch (error: any) {
      console.error('Error updating article status:', error);
      showError(error.message || 'Fehler beim Aktualisieren der Artikel-Status');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // **WORKFLOW HANDLERS**
  const handleCreateWorkflowRequest = async () => {
    if (selectedArticles.length === 0) return;

    try {
      setWorkflowLoading(true);
      const requestData = {
        ...workflowRequestForm,
        articleIds: selectedArticles
      };

      const result = await articleWorkflowApi.createArticleRequest(requestData);
      
      if (result.success) {
        showSuccess(`✅ ${result.emailsSent} Workflow-Anfragen gesendet`);
        setShowWorkflowRequestModal(false);
        setSelectedArticles([]);
        setWorkflowRequestForm({
          articleIds: [],
          requestedFields: ['name', 'description', 'brand', 'gtin', 'weight', 'dimensions', 'category', 'price'],
          message: '',
          priority: 'medium'
        });
        loadData();
      }
    } catch (error: any) {
      showError(`Workflow-Anfrage fehlgeschlagen: ${error.message || 'Unbekannter Fehler'}`);
    } finally {
      setWorkflowLoading(false);
    }
  };

  const handleReviewWorkflowSubmission = async () => {
    if (!selectedSubmission) return;

    try {
      setWorkflowLoading(true);
      
      // Verwende einfache Status-Update API statt komplexer Review-API
      const result = await articleWorkflowApi.updateArticleStatus(
        selectedSubmission.articleId,
        workflowReviewForm.action === 'approved' ? 'approved' : 'rejected',
        workflowReviewForm.comment || (workflowReviewForm.action === 'approved' ? 'Genehmigt' : 'Abgelehnt')
      );

      if (result.success) {
        showSuccess(`✅ Artikel ${workflowReviewForm.action === 'approved' ? 'genehmigt' : 'abgelehnt'}`);
        setShowWorkflowReviewModal(false);
        setSelectedSubmission(null);
        setWorkflowReviewForm({
          action: 'approved',
          comment: '',
          requestedChanges: []
        });
        loadData();
      } else {
        throw new Error(result.error || 'Unbekannter Fehler');
      }
    } catch (error: any) {
      showError(`Fehler: ${error.message || 'Unbekannter Fehler'}`);
    } finally {
      setWorkflowLoading(false);
    }
  };

  // **SELECTION HANDLERS**
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (checked) {
      setSelectedArticles(articles.map(article => article._id));
    } else {
      setSelectedArticles([]);
    }
  };

  const handleSelectArticle = (articleId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (checked) {
      setSelectedArticles(prev => [...prev, articleId]);
    } else {
      setSelectedArticles(prev => prev.filter(id => id !== articleId));
    }
  };

  // **MODAL HANDLERS**
  const handleViewArticle = (articleId: string) => {
    setSelectedArticleId(articleId);
    setShowDetailModal(true);
  };

  const handleEditArticle = (articleId: string) => {
    setSelectedArticleId(articleId);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedArticleId(null);
    loadData(); // Refresh data in case of changes
  };

  // **UTILITY FUNCTIONS**
  const getStatusText = (status: ArticleStatus) => {
    switch (status) {
      case ArticleStatus.APPROVED:
        return 'Genehmigt';
      case ArticleStatus.NEEDS_CORRECTION:
        return 'Korrektur erforderlich';
      case ArticleStatus.DRAFT:
        return 'Entwurf';
      case ArticleStatus.NEW:
        return 'Neu';
      case ArticleStatus.IN_PROGRESS:
        return 'In Bearbeitung';
      case ArticleStatus.SUBMITTED:
        return 'Eingereicht';
      default:
        return 'Unbekannt';
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      approved: { label: 'Genehmigt', class: 'bg-green-100 text-green-800' },
      in_progress: { label: 'In Bearbeitung', class: 'bg-blue-100 text-blue-800' },
      submitted: { label: 'Eingereicht', class: 'bg-yellow-100 text-yellow-800' },
      draft: { label: 'Entwurf', class: 'bg-gray-100 text-gray-800' },
      new: { label: 'Neu', class: 'bg-green-100 text-green-800' },
      rejected: { label: 'Abgelehnt', class: 'bg-red-100 text-red-800' },
      requested: { label: 'Angefordert', class: 'bg-purple-100 text-purple-800' },
      completed: { label: 'Vollständig', class: 'bg-teal-100 text-teal-800' },
      needs_correction: { label: 'Korrektur erforderlich', class: 'bg-red-100 text-red-800' }
    };
    const badge = badges[status as keyof typeof badges] || badges.draft;
    return (
      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${badge.class}`}>
        {badge.label}
      </span>
    );
  };

  const getSortIcon = (column: string) => {
    if (sortBy !== column) return <FontAwesomeIcon icon={faSort} className="text-gray-400" />;
    return <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortUp : faSortDown} className="text-gray-600" />;
  };

  if (loading && !searching) {
    return <LoadingSpinner size="medium" text="Artikel werden geladen..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* **KOMPAKTE TOOLBAR** */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
          {/* **SUCHE** */}
          <div className="relative flex-1">
            <FontAwesomeIcon 
              icon={searching ? faSpinner : faSearch} 
              className={`absolute left-3 top-2.5 text-gray-400 text-sm ${searching ? 'animate-spin' : ''}`} 
            />
            <input
              type="text"
              placeholder="Artikel suchen (Name, Artikelnummer, EK-Code)..."
              value={searchTerm}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent bg-white text-sm"
            />
            {searching && (
              <div className="absolute right-3 top-2.5 text-xs text-gray-500">
                Suche...
              </div>
            )}
          </div>
          
          {/* **FILTER UND AKTIONEN** */}
          <div className="flex gap-2">
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent bg-white text-sm min-w-[120px]"
            >
              <option value="all">Alle Status</option>
              <option value="draft">Entwurf</option>
              <option value="new">Neu</option>
              <option value="in_progress">In Bearbeitung</option>
              <option value="submitted">Eingereicht</option>
              <option value="approved">Genehmigt</option>
              <option value="rejected">Abgelehnt</option>
            </select>

            {/* **FILTER FÜR GELÖSCHTE ARTIKEL** */}
            <select
              value={filters.showDeleted ? 'deleted' : 'active'}
              onChange={(e) => handleFilterChange('showDeleted', e.target.value === 'deleted')}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent bg-white text-sm min-w-[120px]"
            >
              <option value="active">Aktiv</option>
              <option value="deleted">Gelöscht</option>
            </select>

            {/* **LIEFERANTEN-FILTER** */}
            <select
              value={filters.supplierId}
              onChange={(e) => handleFilterChange('supplierId', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent bg-white text-sm min-w-[150px]"
            >
              <option value="all">Alle Lieferanten</option>
              {suppliers.map(supplier => (
                <option key={supplier._id} value={supplier._id}>
                  {supplier.name}
                </option>
              ))}
            </select>

            {/* **FILTER FÜR VERKNÜPFTE LIEFERANTEN** */}
            <select
              value={filters.linkedSuppliersOnly ? 'linked' : 'only'}
              onChange={(e) => handleFilterChange('linkedSuppliersOnly', e.target.value === 'linked')}
              className={`px-3 py-2 border rounded-md text-sm min-w-[140px] ${
                filters.supplierId === 'all' 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200' 
                  : 'bg-white border-gray-300 hover:border-purple-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200'
              }`}
              disabled={filters.supplierId === 'all'}
              title={filters.supplierId === 'all' ? 'Wählen Sie zuerst einen Lieferanten aus' : 'Zeigt verknüpfte Lieferanten mit an'}
            >
              <option value="only">Nur gewählter</option>
              <option value="linked">+ Verknüpfte</option>
            </select>
            
            <button
              onClick={() => setShowImportModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 text-sm"
            >
              <FontAwesomeIcon icon={faUpload} />
              Import
            </button>
            
            <button
              onClick={resetFilters}
              className="px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors text-sm"
              title="Filter zurücksetzen"
            >
              <FontAwesomeIcon icon={faTimesCircle} />
            </button>
          </div>
        </div>
      </div>

      {/* **ERROR MESSAGE** */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.15 }}
        className="px-4 pt-4"
      >
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md flex items-center justify-between text-sm">
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-700"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xs" />
            </button>
          </div>
        )}
      </motion.div>

      {/* **STATISTICS CARDS** */}
      <div className="p-4 pb-2">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 font-medium">Gesamt</p>
                <p className="text-lg font-bold text-blue-900">{liveStats.total}</p>
              </div>
              <div className="text-blue-500 text-sm">
                <FontAwesomeIcon icon={faBoxOpen} />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-600 font-medium">Genehmigt</p>
                <p className="text-lg font-bold text-green-900">{liveStats.approved}</p>
              </div>
              <div className="text-green-500 text-sm">
                <FontAwesomeIcon icon={faCheckCircle} />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-600 font-medium">In Bearbeitung</p>
                <p className="text-lg font-bold text-purple-900">{liveStats.inProgress}</p>
              </div>
              <div className="text-purple-500 text-sm">
                <FontAwesomeIcon icon={faEdit} />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 font-medium">Entwurf</p>
                <p className="text-lg font-bold text-gray-900">{liveStats.draft}</p>
              </div>
              <div className="text-gray-500 text-sm">
                <FontAwesomeIcon icon={faClock} />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-yellow-600 font-medium">Eingereicht</p>
                <p className="text-lg font-bold text-yellow-900">{liveStats.submitted}</p>
              </div>
              <div className="text-yellow-500 text-sm">
                <FontAwesomeIcon icon={faUpload} />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-orange-600 font-medium">Vollständigkeit</p>
                <p className="text-lg font-bold text-orange-900">{Math.round(liveStats.averageCompletionRate)}%</p>
              </div>
              <div className="text-orange-500 text-sm">
                <FontAwesomeIcon icon={faPercent} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* **VERBESSERTE BULK ACTIONS** */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="mx-4 mb-4"
      >
        {selectedArticles.length > 0 && (
          <div className="bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-2 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                    {selectedArticles.length}
                  </div>
                  <span className="text-sm font-semibold text-purple-900">
                    {selectedArticles.length === 1 ? 'Artikel ausgewählt' : 'Artikel ausgewählt'}
                  </span>
                </div>
                
                <div className="h-4 w-px bg-purple-300"></div>
                
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleBulkStatusUpdate(ArticleStatus.APPROVED)}
                    disabled={bulkActionLoading}
                    className="inline-flex items-center px-2 py-1 bg-green-500 text-white rounded text-xs font-medium hover:bg-green-600 disabled:opacity-50 transition-all duration-150 shadow-sm"
                  >
                    <FontAwesomeIcon icon={faCheck} className="mr-1" />
                    Genehmigen
                  </button>

                  <button
                    onClick={() => setShowWorkflowRequestModal(true)}
                    disabled={bulkActionLoading}
                    className="inline-flex items-center px-2 py-1 bg-purple-500 text-white rounded text-xs font-medium hover:bg-purple-600 disabled:opacity-50 transition-all duration-150 shadow-sm"
                  >
                    <FontAwesomeIcon icon={faPaperPlane} className="mr-1" />
                    Workflow-Anfrage
                  </button>

                  <div className="h-4 w-px bg-purple-300"></div>

                  <div className="relative">
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          const supplierId = e.target.value;
                          const supplierArticles = articles
                            .filter(article => 
                              article.supplierId && (
                                typeof article.supplierId === 'object' 
                                  ? article.supplierId._id === supplierId
                                  : article.supplierId === supplierId
                              )
                            )
                            .map(article => article._id);
                          
                          setSelectedArticles(supplierArticles);
                          e.target.value = ''; // Reset selection
                          
                          const supplierName = suppliers.find(s => s._id === supplierId)?.name || 'Unbekannt';
                          showSuccess(`${supplierArticles.length} Artikel von ${supplierName} ausgewählt`);
                        }
                      }}
                      className="inline-flex items-center px-2 py-1 bg-blue-500 text-white rounded text-xs font-medium hover:bg-blue-600 transition-all duration-150 shadow-sm cursor-pointer"
                      defaultValue=""
                    >
                      <option value="" disabled>
                        👥 Alle von Lieferant
                      </option>
                      {suppliers
                        .filter(supplier => 
                          articles.some(article => 
                            article.supplierId && (
                              typeof article.supplierId === 'object' 
                                ? article.supplierId._id === supplier._id
                                : article.supplierId === supplier._id
                            )
                          )
                        )
                        .map(supplier => (
                          <option key={supplier._id} value={supplier._id}>
                            {supplier.name} ({articles.filter(article => 
                              article.supplierId && (
                                typeof article.supplierId === 'object' 
                                  ? article.supplierId._id === supplier._id
                                  : article.supplierId === supplier._id
                              )
                            ).length})
                          </option>
                        ))
                      }
                    </select>
                  </div>
                  
                  <button
                    onClick={handleBulkDelete}
                    disabled={bulkActionLoading}
                    className="inline-flex items-center px-2 py-1 bg-red-500 text-white rounded text-xs font-medium hover:bg-red-600 disabled:opacity-50 transition-all duration-150 shadow-sm"
                  >
                    <FontAwesomeIcon icon={faTrash} className="mr-1" />
                    Löschen
                  </button>
                </div>
              </div>
              
              <button
                onClick={() => setSelectedArticles([])}
                className="text-purple-600 hover:text-purple-800 hover:bg-purple-200 rounded-full p-1 transition-all duration-150"
                title="Auswahl aufheben"
              >
                <FontAwesomeIcon icon={faTimes} className="text-sm" />
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* **CONTENT BEREICH - LISTE MIT RUNDEN CHECKBOXEN** */}
      <div className="p-4">
        {articles.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm">
            <div className="text-gray-400 mb-4">
              <FontAwesomeIcon icon={faBoxOpen} className="text-4xl mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {searchTerm || filters.status !== 'all' 
                  ? 'Keine Artikel gefunden' 
                  : 'Noch keine Artikel vorhanden'}
              </h3>
              <p className="text-gray-500 text-sm max-w-md mx-auto">
                {searchTerm || filters.status !== 'all'
                  ? 'Versuchen Sie andere Suchkriterien oder Filter'
                  : 'Artikel über Excel Import oder Perfion Sync hinzufügen'}
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                      <input
                        type="checkbox"
                        checked={selectedArticles.length === articles.length && articles.length > 0}
                        onChange={handleSelectAll}
                        className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
                      />
                    </th>
                    
                    <th 
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('articleNumber')}
                    >
                      <div className="flex items-center gap-1">
                        Artikelnummer
                        {getSortIcon('articleNumber')}
                      </div>
                    </th>
                    
                    <th 
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center gap-1">
                        Name
                        {getSortIcon('name')}
                      </div>
                    </th>
                    
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lieferant
                    </th>
                    
                    <th 
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center gap-1">
                        Status
                        {getSortIcon('status')}
                      </div>
                    </th>
                    
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vollständigkeit
                    </th>
                    
                    <th 
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('updatedAt')}
                    >
                      <div className="flex items-center gap-1">
                        Aktualisiert
                        {getSortIcon('updatedAt')}
                      </div>
                    </th>
                    
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Workflow
                    </th>
                    
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {articles.map((article) => (
                    <tr 
                      key={article._id} 
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => handleViewArticle(article._id)}
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedArticles.includes(article._id)}
                          onChange={(e) => handleSelectArticle(article._id, e)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
                        />
                      </td>
                      
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        <div className="flex items-center gap-2">
                          <FontAwesomeIcon icon={faBarcode} className="text-gray-400 text-xs" />
                          {article.articleNumber}
                        </div>
                      </td>
                      
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <div className="max-w-xs truncate" title={article.name}>
                          {article.name}
                        </div>
                        {article.brand && (
                          <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                            <FontAwesomeIcon icon={faTags} className="text-xs" />
                            {article.brand}
                          </div>
                        )}
                      </td>
                      
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center gap-2">
                          <span>
                            {article.supplierId && typeof article.supplierId === 'object' 
                              ? article.supplierId.name 
                              : 'Unbekannt'}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const supplierId = article.supplierId && typeof article.supplierId === 'object' 
                                ? article.supplierId._id 
                                : article.supplierId;
                              
                              if (!supplierId) return;
                              
                              const supplierArticles = articles
                                .filter(a => 
                                  a.supplierId && (
                                    typeof a.supplierId === 'object' 
                                      ? a.supplierId._id === supplierId
                                      : a.supplierId === supplierId
                                  )
                                )
                                .map(a => a._id);
                              
                              setSelectedArticles(supplierArticles);
                              
                              const supplierName = article.supplierId && typeof article.supplierId === 'object' 
                                ? article.supplierId.name 
                                : 'Unbekannt';
                              showSuccess(`${supplierArticles.length} Artikel von ${supplierName} ausgewählt`);
                            }}
                            className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50 transition-colors"
                            title="Alle Artikel dieses Lieferanten auswählen"
                            disabled={!article.supplierId}
                          >
                            <FontAwesomeIcon icon={faUsers} className="text-xs" />
                          </button>
                        </div>
                      </td>
                      
                      <td className="px-4 py-3 whitespace-nowrap">
                        {getStatusBadge(article.status)}
                      </td>
                      
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full transition-all ${
                                article.completionPercentage >= 80 ? 'bg-green-500' :
                                article.completionPercentage >= 60 ? 'bg-yellow-500' :
                                article.completionPercentage >= 40 ? 'bg-orange-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${article.completionPercentage}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-600 font-medium">
                            {article.completionPercentage}%
                          </span>
                        </div>
                      </td>
                      
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {new Date(article.updatedAt).toLocaleDateString('de-DE')}
                      </td>
                      
                      {/* **WORKFLOW COLUMN** */}
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-1">
                          {/* **REQUESTED STATUS - Only "Accept" or "Re-request" ** */}
                          {article.status === 'requested' && (
                            <>
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    setWorkflowLoading(true);
                                    const result = await articleWorkflowApi.updateArticleStatus(
                                      article._id, 
                                      'approved',
                                      'Anfrage angenommen - Artikel genehmigt'
                                    );
                                    if (result.success) {
                                      showSuccess('✅ Artikel erfolgreich genehmigt');
                                      loadData();
                                    }
                                  } catch (error: any) {
                                    showError(`Fehler beim Genehmigen: ${error.message}`);
                                  } finally {
                                    setWorkflowLoading(false);
                                  }
                                }}
                                className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50 transition-colors"
                                title="Artikel genehmigen"
                                disabled={workflowLoading}
                              >
                                <FontAwesomeIcon icon={faCheck} className="text-xs" />
                              </button>

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedArticles([article._id]);
                                  setShowWorkflowRequestModal(true);
                                }}
                                className="text-orange-600 hover:text-orange-800 p-1 rounded hover:bg-orange-50 transition-colors"
                                title="Erneut anfragen"
                              >
                                <FontAwesomeIcon icon={faPaperPlane} className="text-xs" />
                              </button>
                            </>
                          )}

                          {/* **NEW STATUS - Accept or Reject ** */}
                          {article.status === 'new' && (
                            <>
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    setWorkflowLoading(true);
                                    const result = await articleWorkflowApi.updateArticleStatus(
                                      article._id, 
                                      'approved',
                                      'Neuer Artikel vom Lieferanten genehmigt'
                                    );
                                    if (result.success) {
                                      showSuccess('✅ Neuer Artikel erfolgreich genehmigt');
                                      loadData();
                                    }
                                  } catch (error: any) {
                                    showError(`Fehler beim Genehmigen: ${error.message}`);
                                  } finally {
                                    setWorkflowLoading(false);
                                  }
                                }}
                                className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50 transition-colors"
                                title="Annehmen"
                                disabled={workflowLoading}
                              >
                                <FontAwesomeIcon icon={faCheck} className="text-xs" />
                              </button>

                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  // Für Ablehnung brauchen wir den Kommentar, also zeigen wir das Modal
                                  setSelectedSubmission({
                                    _id: 'reject-new-article',
                                    articleId: article._id,
                                    submittedBy: 'Supplier',
                                    submittedAt: new Date(),
                                    modifiedFields: ['name', 'description']
                                  });
                                  setWorkflowReviewForm({
                                    action: 'rejected',
                                    comment: '',
                                    requestedChanges: []
                                  });
                                  setShowWorkflowReviewModal(true);
                                }}
                                className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 transition-colors"
                                title="Ablehnen"
                                disabled={workflowLoading}
                              >
                                <FontAwesomeIcon icon={faTimes} className="text-xs" />
                              </button>
                            </>
                          )}

                          {/* **SUBMITTED STATUS - Accept or Reject ** */}
                          {article.status === 'submitted' && (
                            <>
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    setWorkflowLoading(true);
                                    const result = await articleWorkflowApi.updateArticleStatus(
                                      article._id, 
                                      'approved',
                                      'Eingereichte Änderungen genehmigt'
                                    );
                                    if (result.success) {
                                      showSuccess('✅ Artikel erfolgreich genehmigt');
                                      loadData();
                                    }
                                  } catch (error: any) {
                                    showError(`Fehler beim Genehmigen: ${error.message}`);
                                  } finally {
                                    setWorkflowLoading(false);
                                  }
                                }}
                                className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50 transition-colors"
                                title="Annehmen"
                                disabled={workflowLoading}
                              >
                                <FontAwesomeIcon icon={faCheck} className="text-xs" />
                              </button>

                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  // Für Ablehnung brauchen wir den Kommentar, also zeigen wir das Modal
                                  setSelectedSubmission({
                                    _id: 'reject-submission',
                                    articleId: article._id,
                                    submittedBy: 'Supplier',
                                    submittedAt: new Date(),
                                    modifiedFields: ['name', 'description']
                                  });
                                  setWorkflowReviewForm({
                                    action: 'rejected',
                                    comment: '',
                                    requestedChanges: []
                                  });
                                  setShowWorkflowReviewModal(true);
                                }}
                                className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 transition-colors"
                                title="Ablehnen"
                                disabled={workflowLoading}
                              >
                                <FontAwesomeIcon icon={faTimes} className="text-xs" />
                              </button>
                            </>
                          )}

                          {/* **OTHER STATUSES - Only Workflow Request ** */}
                          {!['requested', 'new', 'submitted'].includes(article.status) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedArticles([article._id]);
                                setShowWorkflowRequestModal(true);
                              }}
                              className="text-purple-600 hover:text-purple-800 p-1 rounded hover:bg-purple-50 transition-colors"
                              title="Workflow-Anfrage senden"
                            >
                              <FontAwesomeIcon icon={faPaperPlane} className="text-xs" />
                            </button>
                          )}
                        </div>
                      </td>
                      
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewArticle(article._id);
                            }}
                            className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50 transition-colors"
                            title="Anzeigen"
                          >
                            <FontAwesomeIcon icon={faEye} className="text-xs" />
                          </button>
                          
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditArticle(article._id);
                            }}
                            className="text-gray-600 hover:text-gray-800 p-1 rounded hover:bg-gray-50 transition-colors"
                            title="Bearbeiten"
                          >
                            <FontAwesomeIcon icon={faEdit} className="text-xs" />
                          </button>
                          
                          <button
                            onClick={(e) => handleDeleteArticle(article._id, article.name, e)}
                            className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 transition-colors"
                            title="Löschen"
                          >
                            <FontAwesomeIcon icon={faTrash} className="text-xs" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* **PAGINATION - STICKY BOTTOM BAR** */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40">
        <div className="w-full px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Artikel Information - Links */}
            <div>
              <p className="text-sm text-gray-700">
                Zeige <span className="font-medium">{Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)}</span> bis{' '}
                <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalItems)}</span> von{' '}
                <span className="font-medium">{totalItems}</span> Artikeln
              </p>
            </div>
            
            {/* Seitennummern - Rechts */}
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
                </button>
                
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        pageNum === currentPage
                          ? 'z-10 bg-purple-50 border-purple-500 text-purple-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>


      {/* **MODALS** */}
      {showImportModal && (
        <ArticleImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onImportComplete={loadData}
        />
      )}

      {showDetailModal && selectedArticleId && (
        <ArticleDetailModal
          isOpen={showDetailModal}
          articleId={selectedArticleId}
          onClose={handleCloseDetailModal}
        />
      )}

      {/* **DELETE CONFIRMATION MODAL** */}
      {showDeleteModal && deleteModalData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {deleteModalData.type === 'bulk' ? 'Artikel löschen' : 'Artikel löschen'}
                </h3>
                <p className="text-sm text-gray-600">
                  {deleteModalData.type === 'bulk' 
                    ? `Möchten Sie ${deleteModalData.count} Artikel wirklich löschen?`
                    : `Möchten Sie den Artikel "${deleteModalData.articleName}" wirklich löschen?`
                  }
                </p>
              </div>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleteLoading}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {deleteLoading && <FontAwesomeIcon icon={faSpinner} className="animate-spin" />}
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* **WORKFLOW REQUEST MODAL** */}
      {showWorkflowRequestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <FontAwesomeIcon icon={faPaperPlane} className="text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Workflow-Anfrage erstellen</h3>
                <p className="text-sm text-gray-600">
                  Anfrage für {selectedArticles.length} ausgewählte Artikel senden
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {/* **AUSGEWÄHLTE ARTIKEL ANZEIGEN** */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2 mb-3">
                  <FontAwesomeIcon icon={faBoxOpen} className="text-gray-600" />
                  <h4 className="text-sm font-medium text-gray-900">
                    Ausgewählte Artikel ({selectedArticles.length})
                  </h4>
                </div>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {articles
                    .filter(article => selectedArticles.includes(article._id))
                    .map((article, index) => (
                      <div key={article._id} className="flex items-center gap-3 py-1 text-sm bg-white px-3 py-2 rounded border">
                        <span className="text-gray-500 font-mono text-xs w-6">{index + 1}.</span>
                        <div className="flex items-center gap-2 flex-1">
                          <FontAwesomeIcon icon={faBarcode} className="text-gray-400 text-xs" />
                          <span className="font-medium text-gray-900">{article.articleNumber}</span>
                          <span className="text-gray-600 truncate max-w-xs" title={article.name}>
                            {article.name}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 shrink-0">
                          {article.supplierId && typeof article.supplierId === 'object' 
                            ? article.supplierId.name 
                            : 'Unbekannt'}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Priorität</label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  value={workflowRequestForm.priority}
                  onChange={(e) => setWorkflowRequestForm(prev => ({ ...prev, priority: e.target.value as any }))}
                >
                  <option value="low">Niedrig</option>
                  <option value="medium">Mittel</option>
                  <option value="high">Hoch</option>
                  <option value="urgent">Dringend</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nachricht</label>
                <textarea
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  value={workflowRequestForm.message}
                  onChange={(e) => setWorkflowRequestForm(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Zusätzliche Informationen für den Lieferanten..."
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Deadline (optional)</label>
                <input
                  type="date"
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  value={workflowRequestForm.deadline ? workflowRequestForm.deadline.split('T')[0] : ''}
                  onChange={(e) => setWorkflowRequestForm(prev => ({ 
                    ...prev, 
                    deadline: e.target.value ? new Date(e.target.value).toISOString() : undefined 
                  }))}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowWorkflowRequestModal(false)}
                disabled={workflowLoading}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                onClick={handleCreateWorkflowRequest}
                disabled={workflowLoading}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
              >
                {workflowLoading && <FontAwesomeIcon icon={faSpinner} className="animate-spin" />}
                <FontAwesomeIcon icon={faEnvelope} />
                Anfrage senden
              </button>
            </div>
          </div>
        </div>
      )}

      {/* **WORKFLOW REVIEW MODAL** */}
      {showWorkflowReviewModal && selectedSubmission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-xl w-full mx-4">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <FontAwesomeIcon icon={faUserCheck} className="text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Workflow-Submission Review</h3>
                <p className="text-sm text-gray-600">
                  Eingereichte Änderungen prüfen und bewerten
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Submission Details</h4>
                <div className="text-xs text-gray-600">
                  <p>Eingereicht von: {selectedSubmission.submittedBy}</p>
                  <p>Geänderte Felder: {selectedSubmission.modifiedFields?.join(', ')}</p>
                  <p>Datum: {new Date(selectedSubmission.submittedAt).toLocaleDateString('de-DE')}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Entscheidung</label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={workflowReviewForm.action}
                  onChange={(e) => setWorkflowReviewForm(prev => ({ ...prev, action: e.target.value as any }))}
                >
                  <option value="approved">✅ Genehmigen</option>
                  <option value="rejected">❌ Ablehnen</option>
                  <option value="needs_correction">🔄 Korrektur anfordern</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kommentar für Lieferant 
                  {workflowReviewForm.action === 'rejected' && <span className="text-red-500">*</span>}
                </label>
                <textarea
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={workflowReviewForm.comment}
                  onChange={(e) => setWorkflowReviewForm(prev => ({ ...prev, comment: e.target.value }))}
                  placeholder={
                    workflowReviewForm.action === 'rejected' 
                      ? "Bitte geben Sie einen Ablehnungsgrund ein..."
                      : workflowReviewForm.action === 'needs_correction'
                      ? "Beschreiben Sie die benötigten Korrekturen..."
                      : "Optionales Feedback und Kommentare..."
                  }
                  rows={4}
                  required={workflowReviewForm.action === 'rejected'}
                />
                {workflowReviewForm.action === 'rejected' && (
                  <p className="text-xs text-red-500 mt-1">
                    Ein Kommentar ist bei Ablehnung erforderlich
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowWorkflowReviewModal(false)}
                disabled={workflowLoading}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                onClick={handleReviewWorkflowSubmission}
                disabled={workflowLoading || (workflowReviewForm.action === 'rejected' && !workflowReviewForm.comment?.trim())}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {workflowLoading && <FontAwesomeIcon icon={faSpinner} className="animate-spin" />}
                <FontAwesomeIcon icon={faCheck} />
                {workflowReviewForm.action === 'approved' ? 'Genehmigen' : 
                 workflowReviewForm.action === 'rejected' ? 'Ablehnen' : 
                 'Korrektur anfordern'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Articles;
